import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { DragDropContext, Draggable, DraggableLocation, DropResult, Droppable } from "react-beautiful-dnd";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Accreditation } from "@domain/organization/accreditation";
import { Organization } from "@domain/organization/organization";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { Title } from "@ui/components/containers/title";
import { Spacer, row, stack } from "@ui/components/layout";
import { ClosableModal } from "@ui/modals/closableModal";
import { theme } from "@ui/styles/theme";
import { gap, mobile } from "@ui/styles/utils";

interface AccreditationOrderModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	accreditations: Accreditation[];
	organization: Organization;
}

export const AccreditationOrderModal: React.FC<AccreditationOrderModalProps> = ({
	accreditations,
	organization,
	close,
	...htmlProps
}) => {
	const [orderedAccreditation, setOrderedAccreditation] = useState<Accreditation[]>(accreditations);

	// console.log("orderedAccreditation", orderedAccreditation);
	const findItems = (
		accreditations: Accreditation[],
		result: DraggableLocation
	): { accreditation: Accreditation; index: number } => {
		const accreditation = accreditations.find((acc) => acc.id === result.droppableId) as Accreditation;
		const index = accreditations.findIndex((acc) => acc.id === result.droppableId);

		return {
			accreditation,
			index,
		};
	};

	const sortBlocks = (result: DropResult): void => {
		const { source, destination } = result;
		if (!destination) {
			return;
		}
		if (source.droppableId === destination.droppableId && source.index === destination.index) {
			return;
		}

		const accreditationSource = findItems(orderedAccreditation, source);
		// console.log("accreditationSource===>", accreditationSource);
		const accreditationDestination = findItems(orderedAccreditation, destination);
		// console.log("accreditationDestination===>", accreditationDestination);

		const sections = [...orderedAccreditation];
		sections.splice(accreditationSource.index, 1);
		// console.log("sections", sections);
		const movedSections = [...sections];
		// console.log("movedSections", movedSections);
		movedSections.splice(accreditationDestination.index, 0, accreditationSource.accreditation);
		// console.log("final", final);
		setOrderedAccreditation(movedSections);
	};

	const { organizationService } = useServices();

	const history = useHistory();

	const { loading, run } = useAsync(async () => {
		await organizationService.orderAccreditation(orderedAccreditation, organization.id);
		close();
		history.push(Path.Organization(organization, ":tab?"));
	}, [orderedAccreditation]);

	return (
		<Container close={close} {...htmlProps}>
			<DragDropContext onDragEnd={sortBlocks}>
				<Accreditations>
					{orderedAccreditation?.map((acc, index) => (
						<Droppable key={acc.id} droppableId={acc.id}>
							{(droppableProvided) => (
								<MainItem {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
									<Draggable draggableId={acc.id} index={index}>
										{(pr, sn) => (
											<div ref={pr.innerRef} {...pr.draggableProps}>
												<AccreditationView
													style={sn.isDragging ? { boxShadow: "0 22px 94px 0 rgba(0, 0, 0, 0.15)" } : {}}
												>
													<LogoWrapper>
														<img src={acc.logo.url} />
														{acc.date && (
															<AccreditationDate>
																{I18n.t("organization.accreditation.date.view", { year: acc.date.getFullYear() })}
															</AccreditationDate>
														)}
													</LogoWrapper>
													<div>
														<Title>{acc.name}</Title>
														{/* {acc.description.getPlainText() ? <StyledDraftDescription content={acc.description} /> : null} */}
														<Reference>
															{I18n.t("organization.accreditation.reference.view", { reference: acc.referenceNumber })}
														</Reference>
													</div>
													<Spacer />
												</AccreditationView>
												<DragIcon {...pr.dragHandleProps}>
													<div />
													<div />
												</DragIcon>
											</div>
										)}
									</Draggable>
									{droppableProvided.placeholder}
								</MainItem>
							)}
						</Droppable>
					))}
				</Accreditations>
			</DragDropContext>
			<LoadingButton loading={loading} onClick={() => run()}>
				{I18n.t("save")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(40)};
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
`;

const Accreditations = styled.div`
	${gap(45)};
`;
const MainItem = styled.div``;

const AccreditationView = styled.div`
	${row(20, "flex-start", "center")};
	@media ${mobile} {
		${row(20, "flex-start", "center")};
	}
`;

const DragIcon = styled.div`
	width: 30px;
	padding: 5px;
	${gap(5)};

	margin: 5px auto;
	> div {
		background-color: ${({ theme }) => theme.colors.lightgray};
		height: 1px;
	}
`;

const LogoWrapper = styled.div`
	width: 100px;
	@media ${mobile} {
		width: 80px;
	}
	flex-shrink: 0;
	${stack(10, "center", "center")};

	> img {
		width: 50px;
		height: 50px;
		border-radius: 50%;
		@media ${mobile} {
			max-height: 60px;
		}
	}
`;

const Reference = styled.div`
	${({ theme }) => theme.textStyles.label};
`;

const AccreditationDate = styled.div`
	${({ theme }) => theme.textStyles.text};
	color: ${theme.colors.secondary};
	text-align: center;
`;
